import { Link } from 'react-router-dom'
import HelpPopover from '../help/HelpPopover'
import ClaimScorePill, { claimBandLabel } from './ClaimScorePill'
import { defaultFocusIndex } from './claimFocus'
import type { ProjectClaim } from '../../types'

/**
 * "Claim analysis" in the Evidence lens (figure S2) — the claims of the
 * invention that THIS document supports.
 *
 * This is the Claims lens read backwards, and it comes from the same route:
 * every claim carries the ids of the documents the reader is entitled to see
 * that support it, so keeping the ones that name this document IS the reverse
 * edge. It is deliberately not the per-document novelty_report.claim_analysis
 * the page also holds: those are the claims extracted FROM this file — the
 * one-document view this lens exists to replace — and they carry no per-reader
 * filter, so a document the reader may not see could surface through them.
 *
 * Each row is a way back into the Claims lens, focused on that claim.
 */
/**
 * "4 Highly novel", or "2 Highly novel, 1 Limited novelty" — the counts per
 * band, strongest first, for a set of claims. Unscored claims are counted as
 * "not scored yet" rather than folded into the bottom band, because "we have
 * not looked at it" and "we looked and it is weak" are different answers.
 */
function bandCounts(claims: ProjectClaim[]): string {
  const order = ['Highly novel', 'Moderately novel', 'Limited novelty', 'Low novelty', 'not scored yet']
  const counts = new Map<string, number>()
  for (const c of claims) {
    const band = c.novelty_score == null ? 'not scored yet' : claimBandLabel(c.novelty_score)
    counts.set(band, (counts.get(band) ?? 0) + 1)
  }
  return order.filter((b) => counts.has(b)).map((b) => `${counts.get(b)} ${b}`).join(', ')
}

export interface DocumentClaimsCardProps {
  documentId: string
  projectId?: string
  /** Every claim in the parent invention. Undefined until the claims route lands. */
  claims?: ProjectClaim[]
  /** How many rows to show before the "show all" line. */
  limit?: number
  onShowAll?: () => void
  showingAll?: boolean
}

export default function DocumentClaimsCard({
  documentId,
  projectId,
  claims,
  limit = 4,
  onShowAll,
  showingAll,
}: DocumentClaimsCardProps) {
  const supported = (claims ?? []).map((claim, i) => ({ claim, number: i + 1 }))
    .filter((row) => row.claim.supporting_document_ids.includes(documentId))
  // The figure's header carries the strongest band and its count — "7 claims ·
  // 4 Highly novel". It used to be dropped whenever nothing reached the top
  // band, which left a bare "4 claims" and said nothing about the claims at
  // all. Now it names whichever band leads this document's own set.
  const leadBand = supported.length > 0 ? bandCounts(supported.map((r) => r.claim)).split(', ')[0] : ''
  const shown = showingAll ? supported : supported.slice(0, limit)
  const hidden = supported.length - shown.length
  // The figure puts "Open claim →" on ONE row: the claim the Claims lens would
  // open on, by the same rule the lens itself uses. On every row it is four
  // identical links and no answer to "which claim should I look at?".
  // Computed over the rows ACTUALLY on screen, so the one link is always one
  // the reader can see, even before "Show all".
  const leadRow = defaultFocusIndex(shown.map((r) => r.claim))

  return (
    <section data-part="DocumentClaimsCard" className="light-card p-4">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          <span aria-hidden>🔎</span> <span>Claim analysis</span>
          <HelpPopover
            topic="concept-claim-score"
            title="How a claim is scored"
            body="One label scale for every claim: Highly novel / Moderately novel / Limited novelty / Low novelty, on the 0–100 scale. A claim is scored against the disclosure that supports it — all of it, not any one file."
            size={14}
          />
        </h3>
        <p className="text-[11.5px] text-gray-500">
          {supported.length} claim{supported.length === 1 ? '' : 's'}
          {leadBand ? ` · ${leadBand}` : ''}
        </p>
      </div>

      {supported.length === 0 ? (
        <p className="text-[11.5px] text-gray-500">
          No claim in this invention is recorded as supported by this document yet. They appear here
          once the analysis has read it.
        </p>
      ) : (
        <>
          {shown.map((row, i) => (
            <div key={row.number} className="flex items-center gap-2.5 border-t border-sky-100 py-2 first-of-type:border-t-0">
              <span className="flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-md border border-iflow-200 bg-iflow-50 p-1 text-[11px] font-bold text-iflow-700">
                {row.number}
              </span>
              <span className="min-w-0 flex-1 text-[12.5px] font-semibold leading-snug text-iflow-900">
                {row.claim.claim}
              </span>
              <ClaimScorePill score={row.claim.novelty_score} />
              {projectId && i + 1 === leadRow && (
                <Link
                  to={`/project/${projectId}?lens=claims&focus=claim:${row.number}`}
                  title="Zoom in — Claim"
                  className="shrink-0 whitespace-nowrap text-[11.5px] font-bold text-iflow-600 hover:text-iflow-700"
                >
                  Open claim →
                </Link>
              )}
            </div>
          ))}
          {hidden > 0 && (
            <p className="pt-1.5 text-[11.5px] text-gray-500">
              + {hidden} more claim{hidden === 1 ? '' : 's'}
              {` (${bandCounts(supported.slice(shown.length).map((r) => r.claim))})`}
              {onShowAll && (
                <>
                  {' · '}
                  <button type="button" onClick={onShowAll} className="font-bold text-iflow-600 hover:text-iflow-700">
                    Show all {supported.length}
                  </button>
                </>
              )}
            </p>
          )}
        </>
      )}
    </section>
  )
}
